/**
 * ADMIN MODULE — Team
 * Manage who at Brand Command has admin access (the platform_admins table).
 * Two tiers: "Super admin" (full access + can manage the team) and "Admin"
 * (access only). Super admins invite teammates by email; the invitee is promoted
 * automatically the next time they sign in (claim-on-login, migration 0040).
 * Plain admins see the roster read-only.
 */

import { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout.jsx";
import { supabase } from "../../../shared/utils/supabaseClient.js";
import {
  ADMIN_ROLES,
  ADMIN_ROLE_LABELS,
  myAdminRole,
  listAdmins,
  listPendingInvites,
  inviteAdmin,
  revokeInvite,
  setAdminRole,
  removeAdmin,
} from "../utils/adminTeamService.js";

function fmtDate(s) {
  if (!s) return "";
  const d = new Date(s);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function AdminTeamView() {
  const [role, setRole] = useState(null);          // signed-in staffer's tier
  const [selfId, setSelfId] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");

  // invite form
  const [email, setEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("support");
  const [busy, setBusy] = useState(false);

  const isSuper = role === "superadmin";

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const [r, list, pend] = await Promise.all([
        myAdminRole(),
        listAdmins(),
        listPendingInvites().catch(() => []),
      ]);
      setRole(r);
      setAdmins(list);
      setInvites(pend);
    } catch (e) {
      setErr(e.message || "Could not load the team.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let alive = true;
    supabase.auth.getUser().then(({ data }) => {
      if (alive) setSelfId(data?.user?.id ?? null);
    });
    load();
    return () => { alive = false; };
  }, []);

  function flash(msg) {
    setNote(msg);
    setErr("");
    setTimeout(() => setNote(""), 4000);
  }

  async function onInvite(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await inviteAdmin(email, inviteRole);
      setEmail("");
      setInviteRole("support");
      flash("Invite created. They'll get access the next time they sign in.");
      await load();
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  }

  async function onRevoke(id) {
    setErr("");
    try {
      await revokeInvite(id);
      await load();
    } catch (e2) { setErr(e2.message); }
  }

  async function onChangeRole(userId, newRole) {
    setErr("");
    try {
      await setAdminRole(userId, newRole);
      flash("Role updated.");
      await load();
    } catch (e2) { setErr(e2.message); }
  }

  async function onRemove(userId, who) {
    if (!window.confirm(`Remove admin access for ${who}? They'll keep their login but lose the admin area.`)) return;
    setErr("");
    try {
      await removeAdmin(userId);
      flash("Removed.");
      await load();
    } catch (e2) { setErr(e2.message); }
  }

  return (
    <AdminLayout
      title="Team"
      subtitle="Who at Brand Command can access this admin area."
    >
      {err && <div className="admin-empty" style={{ borderColor: "var(--error)", color: "var(--error)" }}>{err}</div>}
      {note && <div className="admin-empty" style={{ borderColor: "var(--success)", color: "var(--success)" }}>{note}</div>}

      {/* Invite (super admins only) */}
      {isSuper && (
        <div className="admin-card" style={{ marginBottom: 18 }}>
          <div className="admin-panel-head"><h2>Invite a teammate</h2></div>
          <form onSubmit={onInvite} style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label className="admin-field" style={{ flex: "1 1 260px", marginBottom: 0 }}>
              <span>Work email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@brandcommand.app"
                required
              />
            </label>
            <label className="admin-field" style={{ flex: "0 1 200px", marginBottom: 0 }}>
              <span>Access level</span>
              <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                {ADMIN_ROLES.map((r) => (
                  <option key={r} value={r}>{ADMIN_ROLE_LABELS[r]}</option>
                ))}
              </select>
            </label>
            <button className="btn" type="submit" disabled={busy}>{busy ? "Inviting…" : "Send invite"}</button>
          </form>
          <p className="admin-row-meta" style={{ marginTop: 10 }}>
            They keep their normal login. The next time they sign in (or after they sign up), they're
            promoted automatically — no password sharing.
          </p>
        </div>
      )}

      {/* Pending invites */}
      {invites.length > 0 && (
        <div className="admin-card" style={{ marginBottom: 18 }}>
          <div className="admin-panel-head">
            <h2>Pending invites</h2>
            <span className="admin-panel-count">{invites.length}</span>
          </div>
          <div className="admin-list">
            {invites.map((iv) => (
              <div className="admin-row" key={iv.id}>
                <div className="admin-row-top">
                  <span className="admin-row-title">{iv.email}</span>
                  <span className="admin-badge">{ADMIN_ROLE_LABELS[iv.role] ?? iv.role}</span>
                </div>
                <div className="admin-row-top">
                  <span className="admin-row-meta">
                    Invited {fmtDate(iv.created_at)} · expires {fmtDate(iv.expires_at)}
                  </span>
                  {isSuper && (
                    <div className="admin-row-actions">
                      <button className="secondary" onClick={() => onRevoke(iv.id)}>Revoke</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Current team */}
      <div className="admin-card">
        <div className="admin-panel-head">
          <h2>Current team</h2>
          <span className="admin-panel-count">{admins.length}</span>
        </div>

        {loading ? (
          <div className="admin-empty">Loading…</div>
        ) : admins.length === 0 ? (
          <div className="admin-empty"><h3>No admins yet</h3></div>
        ) : (
          <div className="admin-list">
            {admins.map((a) => {
              const isSelf = a.user_id === selfId;
              return (
                <div className="admin-row" key={a.user_id}>
                  <div className="admin-row-top">
                    <span className="admin-row-title">
                      {a.email}{isSelf && <span className="admin-row-meta" style={{ marginLeft: 8 }}>(you)</span>}
                    </span>
                    {(!isSuper || isSelf) && (
                      <span className="admin-badge">{ADMIN_ROLE_LABELS[a.role] ?? a.role}</span>
                    )}
                  </div>
                  <div className="admin-row-top">
                    <span className="admin-row-meta">Joined {fmtDate(a.created_at)}</span>
                    {isSuper && !isSelf && (
                      <div className="admin-row-actions" style={{ alignItems: "center" }}>
                        <select
                          value={a.role}
                          onChange={(e) => onChangeRole(a.user_id, e.target.value)}
                          style={{ width: "auto" }}
                          aria-label={`Role for ${a.email}`}
                        >
                          {ADMIN_ROLES.map((r) => (
                            <option key={r} value={r}>{ADMIN_ROLE_LABELS[r]}</option>
                          ))}
                        </select>
                        <button className="secondary" onClick={() => onRemove(a.user_id, a.email)}>Remove</button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!isSuper && !loading && (
          <p className="admin-row-meta" style={{ marginTop: 12 }}>
            You have <strong>Admin</strong> access. Only a <strong>Super admin</strong> can invite or remove teammates.
          </p>
        )}
      </div>
    </AdminLayout>
  );
}
